/**
 * Report of the rows of DESIGN.md §7.2 that are imported EXACTLY but flagged (class 'flagged') or kept only for
 * RPE (class 'rpeOnly') (W1-07a formats-json-rpe). They are not part of `normalizeRpe`'s report, which lists only
 * what normalisation changed (the export self-check requires that report to be empty for our own output); the
 * import report dialog shows both.
 *
 * Flagged: lead-ins (first event after beat 0 on extrapolated properties, F§6.2), animated incline (Phira freezes
 * it, F§9.4), inert two-point controls with a non-neutral second point (F§12), bezier handles outside [0, 1]
 * (F§6.7), attachUI lines with notes or a texture (F§10.5), lists Phira ignores for drawing (F§9.5), zero-length
 * holds, eased speed under Phira's default integrator (F§7.2; also Phira's own import warning), Bezier curves on
 * speed under that integrator (ignored by Phira; written back without the curve, which stays in phiedit.json, so a
 * later switch to 1.7 speed cannot crash Phira). The last row needs the sidecar entries of the normalisation
 * (`sidecarEntriesOf(result)`), because the IR never holds a Bezier on speed.
 * RPE only: bpmfactor != 1, anchor != [0.5, 0.5] on textured lines, non-default skewControl, isFake values other
 * than 0 / 1 (RPE: only 1 is fake), the note `color` key of RPE 1.7 test builds, unknown keys.
 */

import type { ImportReportEntry, IrChart, IrControlPoint, IrEvent, IrLine } from '../../core/ir/types';
import { Beats } from '../../core/time/beat';
import type { Json } from '../../core/util/types';
import { NormReport } from './normReport';
import type { RpeSidecarEntry } from './normReport';
import type { RpeSpeedMode } from './normalize';
import { LAYER_LIST_KEYS } from './schema';

const EXTRAPOLATED_LAYER_KEYS = ['alphaEvents', 'moveXEvents', 'moveYEvents', 'rotateEvents'] as const;

function startsAfterZero(list: readonly IrEvent<unknown>[] | undefined): boolean {
  const first = list?.[0];
  if (first === undefined) return false;
  const b = Beats.tryFromTriple(first.startTime);
  return b !== null && Beats.lt(Beats.ZERO, b.beat);
}

function isInertNonNeutral(points: readonly IrControlPoint[] | undefined): boolean {
  if (points?.length !== 2) return false;
  const [a, b] = points as readonly [IrControlPoint, IrControlPoint];
  return a.easing === 1 && Math.abs(a.value - 1) < 1e-4 && Math.abs(b.value - 1) >= 1e-4;
}

/**
 * Whether a `skewControl` value is RPE's default (absent, null, or points that all skew by 0): Phira ignores
 * skewControl, and only a non-default one is worth a note. Shared with the checker's PH-22 (UX-090), so the import
 * report and the Problems panel flag the same lines.
 */
export function skewIsDefault(skew: Json | undefined): boolean {
  if (skew === undefined || skew === null) return true;
  if (!Array.isArray(skew)) return false;
  return (skew as readonly Json[]).every((p) => {
    if (p === null || typeof p !== 'object' || Array.isArray(p)) return false;
    const v = (p as { readonly [k: string]: Json }).skew;
    return v === undefined || v === 0;
  });
}

function lineLists(line: IrLine): Array<{ readonly key: string; readonly list: readonly IrEvent<unknown>[] }> {
  const out: Array<{ readonly key: string; readonly list: readonly IrEvent<unknown>[] }> = [];
  for (const layer of line.eventLayers) {
    if (layer === null) continue;
    for (const key of LAYER_LIST_KEYS) {
      const list = layer[key];
      if (list !== undefined) out.push({ key, list });
    }
  }
  const ext = line.extended;
  if (ext !== null && ext !== 'absent') {
    for (const key of [
      'scaleXEvents',
      'scaleYEvents',
      'colorEvents',
      'textEvents',
      'inclineEvents',
      'paintEvents',
      'gifEvents',
    ] as const) {
      const list = ext[key];
      if (list !== undefined) out.push({ key, list });
    }
  }
  return out;
}

const SPEED_BEZIER_KEY = /^eventLayers\[(\d+)\]\.speedEvents\[\d+\]\.bezier$/;

export interface RpeKeptOptions {
  /** Speed integrator Phira uses (info.yml useRpe170Speed); default 'legacy'. */
  readonly speedMode?: RpeSpeedMode;
  /** The normalisation's sidecar entries (`sidecarEntriesOf(result)`), for rows the IR cannot show. */
  readonly sidecar?: readonly RpeSidecarEntry[];
}

/** Flagged and RPE-only rows of a normalised chart (see the module header). */
export function rpeKeptReport(chart: IrChart, opts: RpeKeptOptions = {}): ImportReportEntry[] {
  const r = new NormReport();
  const speedMode = opts.speedMode ?? 'legacy';
  if (speedMode === 'legacy') {
    // Speed Bezier curves under the legacy integrator: one row per line and layer, counting the events.
    for (const entry of opts.sidecar ?? []) {
      const layer = SPEED_BEZIER_KEY.exec(entry.key)?.[1];
      if (layer === undefined) continue;
      r.add(
        'flagged',
        { key: 'rpe.kept.speedBezierLegacy' },
        `judgeLineList[${String(entry.line)}].eventLayers[${layer}].speedEvents`,
        entry.line,
      );
    }
  }
  let unknownKeys = chart.rootExtra.length + chart.META.extra.length;
  for (const b of chart.BPMList) unknownKeys += b.extra.length;
  chart.judgeLineList.forEach((line, li) => {
    const path = `judgeLineList[${String(li)}]`;
    unknownKeys += line.extra.length;
    line.eventLayers.forEach((layer, i) => {
      if (layer === null) return;
      unknownKeys += layer.extra.length;
      for (const key of EXTRAPOLATED_LAYER_KEYS) {
        if (startsAfterZero(layer[key])) {
          r.add('flagged', { key: 'rpe.kept.leadIn' }, `${path}.eventLayers[${String(i)}].${key}`, li);
        }
      }
      if (speedMode === 'legacy' && startsAfterZero(layer.speedEvents)) {
        r.add('flagged', { key: 'rpe.kept.leadIn' }, `${path}.eventLayers[${String(i)}].speedEvents`, li);
      }
      const eased = (layer.speedEvents ?? []).filter((e) => e.easingType > 1).length;
      if (speedMode === 'legacy' && eased > 0) {
        r.add(
          'flagged',
          { key: 'rpe.kept.easedSpeedLegacy' },
          `${path}.eventLayers[${String(i)}].speedEvents`,
          li,
          eased,
        );
      }
    });
    const ext = line.extended;
    if (ext !== null && ext !== 'absent') {
      unknownKeys += ext.extra.length;
      for (const key of ['scaleXEvents', 'scaleYEvents'] as const) {
        if (startsAfterZero(ext[key])) r.add('flagged', { key: 'rpe.kept.leadIn' }, `${path}.extended.${key}`, li);
      }
      const incline = ext.inclineEvents;
      if (incline !== undefined) {
        const values = new Set(incline.flatMap((e) => [e.start, e.end]));
        if (values.size > 1)
          r.add('flagged', { key: 'rpe.kept.inclineAnimated' }, `${path}.extended.inclineEvents`, li);
      }
      for (const [key] of ext.ignoredLists) {
        r.add('flagged', { key: 'rpe.kept.ignoredList', params: { list: key } }, `${path}.extended.${key}`, li);
      }
    }
    for (const { key, list } of lineLists(line)) {
      let handles = 0;
      for (const e of list) {
        unknownKeys += e.extra.length;
        if (
          e.bezier !== 0 &&
          (e.bezierPoints[0] < 0 || e.bezierPoints[0] > 1 || e.bezierPoints[2] < 0 || e.bezierPoints[2] > 1)
        ) {
          handles++;
        }
      }
      if (handles > 0) r.add('flagged', { key: 'rpe.kept.bezierHandles' }, `${path}.${key}`, li, handles);
    }
    for (const curve of ['alphaControl', 'posControl', 'sizeControl', 'yControl'] as const) {
      if (isInertNonNeutral(line[curve])) {
        r.add('flagged', { key: 'rpe.kept.inertControl', params: { curve } }, `${path}.${curve}`, li);
      }
    }
    if (line.attachUI !== null) {
      if (line.notes.length > 0) r.add('flagged', { key: 'rpe.kept.attachUiNotes' }, `${path}.attachUI`, li);
      if (line.textureKind !== 'builtin') r.add('flagged', { key: 'rpe.kept.attachUiTexture' }, `${path}.attachUI`, li);
    }
    let zeroHolds = 0;
    let fakeRaw = 0;
    let noteColor = 0;
    for (const n of line.notes) {
      unknownKeys += n.extra.length;
      const s = Beats.tryFromTriple(n.startTime);
      const e = Beats.tryFromTriple(n.endTime);
      if (n.type === 2 && s !== null && e !== null && Beats.eq(s.beat, e.beat)) zeroHolds++;
      if (n.isFake !== 0 && n.isFake !== 1) fakeRaw++;
      if (n.extra.some(([k]) => k === 'color')) noteColor++;
    }
    if (zeroHolds > 0) r.add('flagged', { key: 'rpe.kept.zeroLengthHold' }, `${path}.notes`, li, zeroHolds);
    if (fakeRaw > 0) r.add('rpeOnly', { key: 'rpe.kept.isFakeRaw' }, `${path}.notes`, li, fakeRaw);
    if (noteColor > 0) r.add('rpeOnly', { key: 'rpe.kept.noteColor' }, `${path}.notes`, li, noteColor);
    if (line.bpmfactor !== 1) {
      r.add('rpeOnly', { key: 'rpe.kept.bpmfactor', params: { value: line.bpmfactor } }, `${path}.bpmfactor`, li);
    }
    if (line.textureKind !== 'builtin' && (line.anchor[0] !== 0.5 || line.anchor[1] !== 0.5)) {
      r.add('rpeOnly', { key: 'rpe.kept.anchor' }, `${path}.anchor`, li);
    }
    if (!skewIsDefault(line.skewControl)) r.add('rpeOnly', { key: 'rpe.kept.skewControl' }, `${path}.skewControl`, li);
  });
  if (unknownKeys > 0) r.add('rpeOnly', { key: 'rpe.kept.unknownKeys' }, undefined, undefined, unknownKeys);
  return r.entries();
}
